/**
 * Why the numbers on a chart or a table do not add up, and why that is fine.
 *
 * A cross-tab over an anonymous survey can show a dash where a total belongs,
 * cells that sum to more than the total row, and a total row smaller than the
 * columns above it. Every one of those is correct and every one of them looks
 * like a bug, so each needs an answer that a reader who has never heard of
 * complementary suppression can act on.
 *
 * Derived here, once, rather than in each renderer: a table and a chart drawn
 * from the same result must give the same reason in the same words, and the
 * wording around withheld cells has to match the lock-glyph copy those two
 * components already share.
 *
 * English, translated at the point of use — the convention chartSummary.ts
 * sets, and it keeps this module free of an i18n dependency.
 */
/**
 * Every note that applies to this result, in reading order.
 *
 * Order is what makes truncation safe: a caller short of room takes the first
 * n rather than choosing, and the ones that explain an arithmetic surprise
 * come before the ones that describe how a figure was drawn.
 */
export const dataNotes = (result, context = {}) => {
    if (!result?.meta)
        return [];
    const notes = [];
    const { notApplicableRespondents, respondentsInScope, baseRespondents, multiValueDimensions, suppressedCellCount, minCellCount, truncated, } = result.meta;
    if (notApplicableRespondents > 0) {
        notes.push({
            id: 'notApplicable',
            short: '{{excluded}} of {{total}} responses said this did not apply and are not counted.',
            long: '{{excluded}} people were asked this but said it did not apply to them. They are left out, so percentages divide by {{base}} rather than {{total}}. Counting them would understate every figure.',
            values: {
                excluded: notApplicableRespondents,
                total: respondentsInScope,
                base: baseRespondents,
            },
        });
    }
    if (multiValueDimensions.length > 0) {
        notes.push({
            id: 'multiValue',
            short: 'Respondents could choose more than one answer, so the cells add up to more than {{count}}.',
            long: 'People could pick more than one answer here, so one person can appear in several places. That makes the cells add up to more than the number of people. The total counts people, not answers, which is why it is smaller than the cells above it.',
            values: { count: respondentsInScope },
        });
    }
    if (suppressedCellCount > 0) {
        notes.push({
            id: 'hiddenCells',
            short: 'Cells with fewer than {{min}} responses are hidden.',
            long: 'This survey is anonymous. A figure covering only a few people can identify them — "2 of 3 in this team said yes" is close to a name — so anything with fewer than {{min}} responses behind it is withheld and shows a dash.',
            values: { min: minCellCount },
        });
    }
    // Separate from hiddenCells on purpose: the causes differ, and a result can
    // have either without the other.
    if (result.totals?.incomplete) {
        notes.push({
            id: 'hiddenTotals',
            short: 'Some totals are hidden.',
            long: 'A total is withheld whenever its row or column contains a hidden figure. Publishing it would give that figure away by subtraction — take the cells you can see away from the total and the missing one falls out. Withholding the total is what keeps the cell hidden.',
        });
    }
    if (context.countOnly) {
        notes.push({
            id: 'countOnly',
            short: 'Too few responses for a percentage — showing counts.',
            long: 'A percentage of a handful of people reads as far more precise than it is: one person changing their mind would move it by tens of points. Counts are shown instead until there are enough responses for a percentage to mean something.',
        });
    }
    if (context.hasIntervals) {
        notes.push({
            id: 'intervals',
            short: 'Bars show the 95% range.',
            long: 'The line through each bar is the range the true figure is very likely to sit in, given how many people answered. Where two bars overlap, this survey cannot tell them apart — the difference you can see is within what chance alone would produce.',
        });
    }
    /**
     * A chart asked to be compared and was not.
     *
     * Only a stat figure drew its own comparison, so on a chart or a table the
     * request simply vanished: the control was on, nothing was different, and
     * nothing said why. Derived here so both renderers say the same thing
     * through the note seam they already have.
     */
    const comparison = result.meta.comparison;
    if (comparison && !comparison.applied) {
        const earlier = comparison.mode === 'previousYear' ? 'a year earlier' : 'the previous period';
        notes.push({
            id: 'comparisonUnavailable',
            short: 'No comparison with {{earlier}}.',
            long: comparison.reason
                ? `${comparison.reason} Nothing on this chart has changed as a result — it shows the period you chose, uncompared.`
                : 'There is nothing to compare this against, so the chart shows the period you chose, uncompared.',
            values: { earlier },
        });
    }
    /**
     * Some cells moved and cannot say so.
     *
     * Withheld rather than absent: the earlier figure was too small to publish,
     * and a change beside a published figure would give it back by subtraction.
     */
    if (comparison?.withheldComparisons) {
        notes.push({
            id: 'comparisonWithheld',
            short: '{{count}} changes are not shown.',
            long: 'For {{count}} of these, the earlier figure covered too few people to publish. Showing how much they changed would give that figure back — subtract the change from what you see here and you have the number that was withheld.',
            values: { count: comparison.withheldComparisons },
        });
    }
    if (truncated) {
        notes.push({
            id: 'truncated',
            short: 'Too many responses to chart in full.',
            long: 'This question has more responses than one chart can be drawn from safely, so nothing is shown rather than a picture of an arbitrary slice. Narrow the date range to bring it back.',
        });
    }
    return notes;
};
export default dataNotes;
